import { useEffect, useRef, useState } from 'react';
import { signalRService } from '../../../services/signalr/SignalRService';
import { HubEvents, HubMethods, SocialEventEnvelope } from '../../../services/signalr/types';
import './FlexoCommander.css';

/**
 * Flexo Commander — a minimal but complete co-op RTS testbed (Supreme-Commander-ish), rendered on
 * a 2D <canvas> in the browser. TWO allied players (blue) vs a simple AI (red).
 *
 * NETCODE (host-authoritative, over the lightweight SocialEvent relay — NO DB, NO backend rule
 * module; see RoomHub.Social.cs rts_* cases):
 *   - Whoever presses "Start battle" becomes the HOST: it sends `rts_start` (relayed to the whole
 *     room group). Every client sets hostId = envelope.from; the one whose id matches runs the sim.
 *   - The HOST runs a fixed-timestep sim (~6 ticks/sec) and each tick broadcasts a compact
 *     `rts_state` snapshot (units/mass/nodes/status) to everyone-ELSE (server uses OthersInGroup).
 *   - A GUEST never simulates: it renders the latest `rts_state` and sends `rts_cmd` (orders:
 *     join / move / build / again) to the host, which applies them to the authoritative sim.
 *   - `rts_end` announces the final result (belt-and-suspenders alongside the snapshot status).
 *
 * The RTS deliberately does NOT use the game_delta / GameSessionState path (that would return
 * unknown_kind — there is no "rts" rule module). GamePanel mounts this component when it receives
 * the present-on-entry `game_state` with kind === 'rts' (SendInitialGameStatesToCallerAsync pushes
 * it regardless of the rule registry). Sparks are NOT minted for this testbed (no win-edge rule).
 */

// ---------------------------------------------------------------------------
// Sim constants (mirror flexo_commander_config.json GameDefinitions[0].Params).
// ---------------------------------------------------------------------------
const MAP_W = 32;
const MAP_H = 24;
const START_MASS = 60;
const WS = 20; // world-units → canvas pixels
const CANVAS_W = MAP_W * WS; // 640
const CANVAS_H = MAP_H * WS; // 480
const TICK_MS = 166; // ~6 ticks/sec
const DT = TICK_MS / 1000;
const MAX_PER_SIDE = 40;

type Team = 'blue' | 'red';
type UType = 'commander' | 'tank' | 'extractor' | 'factory' | 'turret';
type BType = 'extractor' | 'factory' | 'turret';

interface UStat { hp: number; range: number; dmg: number; speed: number; cd: number; r: number }
const STATS: Record<UType, UStat> = {
  commander: { hp: 900, range: 6, dmg: 22, speed: 2.2, cd: 1.0, r: 0.9 },
  tank: { hp: 110, range: 5, dmg: 12, speed: 3.4, cd: 0.8, r: 0.5 },
  turret: { hp: 260, range: 7, dmg: 16, speed: 0, cd: 0.7, r: 0.6 },
  factory: { hp: 420, range: 0, dmg: 0, speed: 0, cd: 0, r: 0.9 },
  extractor: { hp: 160, range: 0, dmg: 0, speed: 0, cd: 0, r: 0.7 },
};
const COST: Record<BType | 'tank', number> = { extractor: 25, factory: 60, turret: 45, tank: 25 };
const BUILD_TIME: Record<BType, number> = { extractor: 4, factory: 6, turret: 4 };
const BASE_TRICKLE = 2; // mass/sec/player (commander trickle)
const EXTRACTOR_RATE = 6; // mass/sec per built extractor sitting on a node
const RED_TRICKLE = 5;
const FACTORY_PROD = 5; // sec per blue tank

const NODES_XY: { x: number; y: number }[] = [
  { x: 8, y: 18 }, { x: 14, y: 14 }, { x: 6, y: 10 }, { x: 16, y: 20 }, { x: 12, y: 6 },
  { x: 20, y: 10 }, { x: 24, y: 16 }, { x: 18, y: 4 }, { x: 26, y: 8 }, { x: 10, y: 22 },
];

// ---------------------------------------------------------------------------
// Sim types
// ---------------------------------------------------------------------------
interface Unit {
  id: number; team: Team; type: UType; owner: string;
  x: number; y: number; hp: number; maxHp: number;
  tx: number | null; ty: number | null; atk: boolean;
  cd: number; bp: number; prod: number; node: number;
}
interface MNode { x: number; y: number; taken: Team | null }
interface SimState {
  tick: number; status: 'active' | 'won' | 'lost'; nextId: number;
  units: Unit[]; mass: Record<string, number>; redMass: number;
  nodes: MNode[]; players: string[]; elapsed: number;
}

// Wire (compact) shapes for rts_state / rts_cmd payloads.
interface WireUnit { id: number; tm: Team; ty: UType; o: string; x: number; y: number; hp: number; mhp: number; bl: boolean }
interface WirePayload {
  action?: string; ids?: number[]; x?: number; y?: number; atk?: boolean; btype?: BType; result?: string;
  t?: number; st?: 'active' | 'won' | 'lost'; units?: WireUnit[]; mass?: Record<string, number>; nodes?: MNode[];
}

// Render model shared by host (from sim) and guest (from snapshot).
interface RUnit { id: number; team: Team; type: UType; owner: string; x: number; y: number; hp: number; maxHp: number; built: boolean }
interface RenderModel { units: RUnit[]; nodes: MNode[]; mass: Record<string, number>; status: string }

// ---------------------------------------------------------------------------
// Pure sim helpers (host only)
// ---------------------------------------------------------------------------
const clampX = (x: number) => Math.max(0.5, Math.min(MAP_W - 0.5, x));
const clampY = (y: number) => Math.max(0.5, Math.min(MAP_H - 0.5, y));
const dist2 = (ax: number, ay: number, bx: number, by: number) => {
  const dx = ax - bx, dy = ay - by; return dx * dx + dy * dy;
};
const isBuilding = (t: UType) => t === 'extractor' || t === 'factory' || t === 'turret';
const isBuilt = (u: Unit) => (isBuilding(u.type) ? u.bp >= BUILD_TIME[u.type as BType] : true);
const countTeam = (s: SimState, team: Team) => s.units.reduce((c, u) => c + (u.team === team ? 1 : 0), 0);

function mk(s: SimState, type: UType, team: Team, owner: string, x: number, y: number): Unit {
  const st = STATS[type];
  return {
    id: s.nextId++, team, type, owner, x: clampX(x), y: clampY(y),
    hp: st.hp, maxHp: st.hp, tx: null, ty: null, atk: false, cd: 0,
    // mobile units + commander are "already built"; buildings ramp bp 0 → BUILD_TIME.
    bp: isBuilding(type) ? 0 : 9999, prod: 0, node: -1,
  };
}

function addPlayer(s: SimState, uid: string): void {
  if (s.players.includes(uid)) return;
  s.players.push(uid);
  s.mass[uid] = START_MASS;
  const idx = s.players.length - 1;
  s.units.push(mk(s, 'commander', 'blue', uid, 4 + idx * 2.4, MAP_H - 4 - idx * 1.6));
}

function initSim(hostUid: string): SimState {
  const s: SimState = {
    tick: 0, status: 'active', nextId: 1, units: [], mass: {}, redMass: 80,
    nodes: NODES_XY.map((n) => ({ x: n.x, y: n.y, taken: null })), players: [], elapsed: 0,
  };
  addPlayer(s, hostUid);
  // Red base (top-right): commander + a pre-built factory / extractor / turret.
  const rx = MAP_W - 4, ry = 4;
  s.units.push(mk(s, 'commander', 'red', 'ai', rx, ry));
  const f = mk(s, 'factory', 'red', 'ai', rx - 1.8, ry + 1.8); f.bp = BUILD_TIME.factory; s.units.push(f);
  const e = mk(s, 'extractor', 'red', 'ai', rx + 1, ry - 1); e.bp = BUILD_TIME.extractor; s.units.push(e);
  const t = mk(s, 'turret', 'red', 'ai', rx - 2.5, ry - 1); t.bp = BUILD_TIME.turret; s.units.push(t);
  return s;
}

function nearestEnemy(s: SimState, u: Unit, range: number): Unit | null {
  let best: Unit | null = null, bd = Infinity;
  for (const e of s.units) {
    if (e.team === u.team || e.hp <= 0) continue;
    const rr = range + STATS[e.type].r;
    const d = dist2(u.x, u.y, e.x, e.y);
    if (d <= rr * rr && d < bd) { bd = d; best = e; }
  }
  return best;
}
function nearestOfTeam(s: SimState, u: Unit, team: Team): Unit | null {
  let best: Unit | null = null, bd = Infinity;
  for (const e of s.units) {
    if (e.team !== team || e.hp <= 0) continue;
    const d = dist2(u.x, u.y, e.x, e.y);
    if (d < bd) { bd = d; best = e; }
  }
  return best;
}

function spawnTank(s: SimState, team: Team, owner: string, x: number, y: number): void {
  const a = Math.random() * Math.PI * 2;
  s.units.push(mk(s, 'tank', team, owner, x + Math.cos(a) * 1.3, y + Math.sin(a) * 1.3));
}

/** Advance the authoritative sim by one fixed tick. Host only. */
function stepSim(s: SimState): void {
  if (s.status !== 'active') return;
  s.tick++; s.elapsed += DT;
  const redProdRate = Math.max(2.5, 7 - Math.floor(s.elapsed / 20) * 0.7); // red escalates over time

  // --- economy (commander trickle + built extractors) ---
  for (const uid of s.players) s.mass[uid] = (s.mass[uid] ?? 0) + BASE_TRICKLE * DT;
  s.redMass += RED_TRICKLE * DT;
  for (const u of s.units) {
    if (u.type === 'extractor' && isBuilt(u)) {
      const rate = u.node >= 0 ? EXTRACTOR_RATE : EXTRACTOR_RATE * 0.4;
      if (u.team === 'blue') s.mass[u.owner] = (s.mass[u.owner] ?? 0) + rate * DT;
      else s.redMass += rate * DT;
    }
  }

  // --- construction progress ---
  for (const u of s.units) {
    if (isBuilding(u.type)) {
      const bt = BUILD_TIME[u.type as BType];
      if (u.bp < bt) u.bp = Math.min(bt, u.bp + DT);
    }
  }

  // --- factory production ---
  for (const u of s.units) {
    if (u.type !== 'factory' || !isBuilt(u)) continue;
    u.prod += DT;
    if (u.team === 'blue') {
      if (u.prod >= FACTORY_PROD && (s.mass[u.owner] ?? 0) >= COST.tank && countTeam(s, 'blue') < MAX_PER_SIDE) {
        u.prod = 0; s.mass[u.owner] -= COST.tank; spawnTank(s, 'blue', u.owner, u.x, u.y);
      }
    } else if (u.prod >= redProdRate && s.redMass >= COST.tank && countTeam(s, 'red') < MAX_PER_SIDE) {
      u.prod = 0; s.redMass -= COST.tank; spawnTank(s, 'red', 'ai', u.x, u.y);
    }
  }

  // --- red AI: attack-move idle tanks toward the nearest blue thing (refresh ~1/sec) ---
  for (const u of s.units) {
    if (u.team !== 'red' || u.type !== 'tank') continue;
    if (u.tx === null || s.tick % 6 === 0) {
      const target = nearestOfTeam(s, u, 'blue');
      if (target) { u.tx = target.x; u.ty = target.y; u.atk = true; }
    }
  }

  // --- combat + movement (one pass; damage applies immediately) ---
  for (const u of s.units) {
    const st = STATS[u.type];
    let enemy: Unit | null = null;
    if (st.dmg > 0) {
      u.cd = Math.max(0, u.cd - DT);
      enemy = nearestEnemy(s, u, st.range);
      if (enemy && u.cd <= 0) { enemy.hp -= st.dmg; u.cd = st.cd; }
    }
    if (st.speed > 0 && u.tx !== null && u.ty !== null) {
      const hold = u.atk && enemy !== null; // attack-move units halt to fire
      if (!hold) {
        const dx = u.tx - u.x, dy = u.ty - u.y;
        const d = Math.hypot(dx, dy);
        const step = st.speed * DT;
        if (d <= step) { u.x = u.tx; u.y = u.ty; u.tx = null; u.ty = null; }
        else { u.x += (dx / d) * step; u.y += (dy / d) * step; }
      }
    }
  }

  // --- remove dead (free the node any dead extractor held) ---
  s.units = s.units.filter((u) => {
    if (u.hp > 0) return true;
    if (u.type === 'extractor' && u.node >= 0 && s.nodes[u.node]) s.nodes[u.node].taken = null;
    return false;
  });

  // --- win / lose ---
  const redCmd = s.units.some((u) => u.team === 'red' && u.type === 'commander');
  const blueCmd = s.units.some((u) => u.team === 'blue' && u.type === 'commander');
  if (!redCmd) s.status = 'won';
  else if (!blueCmd) s.status = 'lost';
}

// --- command application (host applies its own + relayed guest orders) ---
function applyBuild(s: SimState, uid: string, btype: BType, x: number, y: number): void {
  if (!s.players.includes(uid)) return;
  if (!s.units.some((u) => u.team === 'blue' && u.type === 'commander' && u.hp > 0)) return; // need a commander alive
  const cost = COST[btype];
  if ((s.mass[uid] ?? 0) < cost) return;
  let px = clampX(x), py = clampY(y), node = -1;
  if (btype === 'extractor') {
    // Snap onto the nearest FREE node within ~3 units so extractors sit on resources.
    let best = -1, bd = 9;
    s.nodes.forEach((n, i) => { if (n.taken) return; const d = dist2(px, py, n.x, n.y); if (d < bd) { bd = d; best = i; } });
    if (best >= 0) { node = best; s.nodes[best].taken = 'blue'; px = s.nodes[best].x; py = s.nodes[best].y; }
  }
  s.mass[uid] -= cost;
  const u = mk(s, btype, 'blue', uid, px, py); u.node = node; s.units.push(u);
}
function applyMove(s: SimState, ids: number[], x: number, y: number, atk: boolean): void {
  const set = new Set(ids);
  for (const u of s.units) {
    if (u.team === 'blue' && set.has(u.id) && STATS[u.type].speed > 0) {
      u.tx = clampX(x); u.ty = clampY(y); u.atk = atk;
    }
  }
}
function applyCmd(s: SimState, action: string, p: WirePayload, fromUid: string): void {
  if (action === 'join') addPlayer(s, fromUid);
  else if (action === 'move') applyMove(s, p.ids ?? [], p.x ?? 0, p.y ?? 0, !!p.atk);
  else if (action === 'build' && p.btype) applyBuild(s, fromUid, p.btype, p.x ?? 0, p.y ?? 0);
  // 'again' is handled by the host component (full reset), not here.
}

// --- (de)serialization between sim / wire / render model ---
function toWire(s: SimState): WirePayload {
  return {
    t: s.tick, st: s.status,
    units: s.units.map((u) => ({
      id: u.id, tm: u.team, ty: u.type, o: u.owner,
      x: Math.round(u.x * 100) / 100, y: Math.round(u.y * 100) / 100,
      hp: Math.round(u.hp), mhp: u.maxHp, bl: isBuilt(u),
    })),
    mass: Object.fromEntries(Object.entries(s.mass).map(([k, v]) => [k, Math.round(v)])),
    nodes: s.nodes,
  };
}
function simToModel(s: SimState): RenderModel {
  return {
    units: s.units.map((u) => ({
      id: u.id, team: u.team, type: u.type, owner: u.owner,
      x: u.x, y: u.y, hp: u.hp, maxHp: u.maxHp, built: isBuilt(u),
    })),
    nodes: s.nodes, mass: s.mass, status: s.status,
  };
}
function wireToModel(p: WirePayload): RenderModel {
  return {
    units: (p.units ?? []).map((w) => ({
      id: w.id, team: w.tm, type: w.ty, owner: w.o, x: w.x, y: w.y, hp: w.hp, maxHp: w.mhp, built: w.bl,
    })),
    nodes: p.nodes ?? [], mass: p.mass ?? {}, status: p.st ?? 'active',
  };
}

// --- rendering ---
interface DragState { active: boolean; sx: number; sy: number; cx: number; cy: number; moved: boolean; pointerType: string }
const GLYPH: Record<BType, string> = { extractor: '⬢', factory: '▦', turret: '◈' };

function draw(ctx: CanvasRenderingContext2D, m: RenderModel, sel: Set<number>, drag: DragState | null): void {
  ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.fillStyle = '#0b1018'; ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.strokeStyle = 'rgba(90,120,160,0.08)'; ctx.lineWidth = 1;
  for (let gx = 0; gx <= MAP_W; gx += 2) { ctx.beginPath(); ctx.moveTo(gx * WS, 0); ctx.lineTo(gx * WS, CANVAS_H); ctx.stroke(); }
  for (let gy = 0; gy <= MAP_H; gy += 2) { ctx.beginPath(); ctx.moveTo(0, gy * WS); ctx.lineTo(CANVAS_W, gy * WS); ctx.stroke(); }

  for (const n of m.nodes) {
    const x = n.x * WS, y = n.y * WS;
    ctx.save(); ctx.translate(x, y); ctx.rotate(Math.PI / 4);
    ctx.fillStyle = n.taken ? 'rgba(240,200,90,0.30)' : 'rgba(240,200,90,0.85)';
    ctx.fillRect(-5, -5, 10, 10); ctx.restore();
  }

  for (const u of m.units) {
    const x = u.x * WS, y = u.y * WS;
    const col = u.team === 'blue' ? '#4aa3ff' : '#ff5a4a';
    ctx.globalAlpha = u.built ? 1 : 0.5;
    const r = STATS[u.type].r * WS;
    if (u.type === 'commander') {
      ctx.fillStyle = col; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke();
      ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.fill();
    } else if (u.type === 'tank') {
      ctx.fillStyle = col; ctx.beginPath();
      ctx.moveTo(x, y - r); ctx.lineTo(x - r, y + r); ctx.lineTo(x + r, y + r); ctx.closePath(); ctx.fill();
    } else {
      ctx.fillStyle = col; ctx.fillRect(x - r, y - r, r * 2, r * 2);
      ctx.strokeStyle = 'rgba(0,0,0,0.4)'; ctx.lineWidth = 1; ctx.strokeRect(x - r, y - r, r * 2, r * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.font = `${Math.round(r * 1.2)}px sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(GLYPH[u.type as BType], x, y);
    }
    ctx.globalAlpha = 1;
    if (sel.has(u.id)) {
      ctx.strokeStyle = '#6bff9e'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(x, y, r + 4, 0, Math.PI * 2); ctx.stroke();
    }
    if (u.hp < u.maxHp) {
      const bw = Math.max(14, r * 2), bh = 3, by = y - r - 7;
      ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(x - bw / 2, by, bw, bh);
      ctx.fillStyle = u.team === 'blue' ? '#6bff9e' : '#ff8a6b';
      ctx.fillRect(x - bw / 2, by, bw * Math.max(0, u.hp / u.maxHp), bh);
    }
  }

  if (drag && drag.active && drag.moved) {
    const x0 = Math.min(drag.sx, drag.cx), y0 = Math.min(drag.sy, drag.cy);
    const w = Math.abs(drag.cx - drag.sx), h = Math.abs(drag.cy - drag.sy);
    ctx.strokeStyle = 'rgba(107,255,158,0.9)'; ctx.lineWidth = 1; ctx.strokeRect(x0, y0, w, h);
    ctx.fillStyle = 'rgba(107,255,158,0.12)'; ctx.fillRect(x0, y0, w, h);
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
type Role = 'none' | 'host' | 'guest';

export function FlexoCommander({ roomId, userId }: { roomId: string; gameId: string; userId?: string }) {
  const uid = userId || 'me';

  const [started, setStarted] = useState(false);
  const [role, setRole] = useState<Role>('none');
  const [banner, setBanner] = useState<'won' | 'lost' | null>(null);
  const [buildMode, setBuildMode] = useState<BType | null>(null);
  const [selCount, setSelCount] = useState(0);
  const [selHasCmd, setSelHasCmd] = useState(false);
  const [hud, setHud] = useState<{ mass: number; blue: number; red: number }>({ mass: 0, blue: 0, red: 0 });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const simRef = useRef<SimState | null>(null);
  const snapRef = useRef<RenderModel | null>(null);
  const roleRef = useRef<Role>('none');
  const startedRef = useRef(false);
  const selRef = useRef<Set<number>>(new Set());
  const buildModeRef = useRef<BType | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const intervalRef = useRef<number | null>(null);
  const endSentRef = useRef(false);
  const handlerRef = useRef<(raw: string) => void>(() => {});

  buildModeRef.current = buildMode;

  // --- wire helpers ---
  const sendRts = (type: string, payload: Record<string, unknown>): void => {
    const env = JSON.stringify({ type, payloadJson: JSON.stringify(payload) });
    signalRService.invoke<string>(HubMethods.SocialEvent, roomId, env).catch(() => { /* fire-and-forget */ });
  };

  const getModel = (): RenderModel | null =>
    roleRef.current === 'host' ? (simRef.current ? simToModel(simRef.current) : null) : snapRef.current;

  const setSelection = (ids: number[]): void => {
    selRef.current = new Set(ids);
    setSelCount(ids.length);
    const m = getModel();
    setSelHasCmd(!!m && ids.some((id) => m.units.find((u) => u.id === id)?.type === 'commander'));
  };

  // --- host loop ---
  const broadcastState = (s: SimState): void => { sendRts('rts_state', toWire(s) as unknown as Record<string, unknown>); };

  const hostTick = (): void => {
    const s = simRef.current;
    if (!s) return;
    stepSim(s);
    broadcastState(s);
    setHud({ mass: Math.floor(s.mass[uid] ?? 0), blue: countTeam(s, 'blue'), red: countTeam(s, 'red') });
    if (s.status !== 'active' && !endSentRef.current) {
      endSentRef.current = true;
      broadcastState(s); // ensure the terminal snapshot lands
      sendRts('rts_end', { result: s.status });
      setBanner(s.status);
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    }
  };
  const startLoop = (): void => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = window.setInterval(hostTick, TICK_MS);
  };

  const becomeHost = (): void => {
    roleRef.current = 'host'; setRole('host');
    simRef.current = initSim(uid);
    endSentRef.current = false;
    startLoop();
  };
  const becomeGuest = (): void => {
    roleRef.current = 'guest'; setRole('guest');
    sendRts('rts_cmd', { action: 'join' });
  };
  const hostReset = (): void => {
    const prev = simRef.current;
    const players = prev ? prev.players.slice() : [uid];
    const s = initSim(uid);
    for (const pid of players) if (pid !== uid) addPlayer(s, pid);
    simRef.current = s;
    endSentRef.current = false;
    setBanner(null);
    startLoop();
  };

  // --- inbound envelope handling (kept fresh via handlerRef so the subscription is stable) ---
  handlerRef.current = (raw: string): void => {
    let env: SocialEventEnvelope;
    try { env = JSON.parse(raw) as SocialEventEnvelope; } catch { return; }
    if (!env?.type || !env.type.startsWith('rts_')) return;
    let p: WirePayload = {};
    try { p = env.payloadJson ? (JSON.parse(env.payloadJson) as WirePayload) : {}; } catch { /* ignore */ }
    const from = env.from;

    if (env.type === 'rts_start') {
      if (startedRef.current) return;
      startedRef.current = true; setStarted(true);
      if (from === uid) becomeHost(); else becomeGuest();
    } else if (env.type === 'rts_state') {
      // A late guest that never saw rts_start auto-joins an in-progress battle here.
      if (!startedRef.current) { startedRef.current = true; setStarted(true); becomeGuest(); }
      if (roleRef.current === 'host') return; // host is authoritative; ignore echoes
      const m = wireToModel(p); snapRef.current = m;
      setHud({ mass: Math.floor(m.mass[uid] ?? 0), blue: m.units.filter((u) => u.team === 'blue').length, red: m.units.filter((u) => u.team === 'red').length });
      if (m.status === 'won' || m.status === 'lost') setBanner(m.status);
    } else if (env.type === 'rts_cmd') {
      if (roleRef.current !== 'host' || !simRef.current) return;
      if (p.action === 'again') { hostReset(); return; }
      applyCmd(simRef.current, p.action ?? '', p, from);
    } else if (env.type === 'rts_end') {
      if (p.result === 'won' || p.result === 'lost') setBanner(p.result);
    }
  };

  // Stable subscription (once) + teardown.
  useEffect(() => {
    const unsub = signalRService.on(HubEvents.SocialEventReceived, (...args: unknown[]) => handlerRef.current(args[0] as string));
    return () => {
      unsub();
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Continuous canvas draw loop (both roles render from whichever model source is live).
  useEffect(() => {
    let raf = 0;
    const loop = (): void => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (ctx) {
        const m = getModel();
        if (m) draw(ctx, m, selRef.current, dragRef.current);
      }
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- order dispatch (host applies locally; guest ships to host) ---
  const dispatch = (action: string, payload: Record<string, unknown>): void => {
    if (roleRef.current === 'host' && simRef.current) {
      applyCmd(simRef.current, action, payload as WirePayload, uid);
    } else {
      sendRts('rts_cmd', { action, ...payload });
    }
  };
  const issueMove = (x: number, y: number, atk: boolean): void => {
    const ids = [...selRef.current];
    if (!ids.length) return;
    dispatch('move', { ids, x, y, atk });
  };
  const issueBuild = (btype: BType, x: number, y: number): void => { dispatch('build', { btype, x, y }); };

  // --- canvas input (desktop mouse + touch; 44px control targets in the HTML bar) ---
  const toCanvas = (clientX: number, clientY: number): { x: number; y: number } => {
    const c = canvasRef.current;
    if (!c) return { x: 0, y: 0 };
    const rect = c.getBoundingClientRect();
    return { x: (clientX - rect.left) * (CANVAS_W / rect.width), y: (clientY - rect.top) * (CANVAS_H / rect.height) };
  };
  const unitAt = (m: RenderModel, wx: number, wy: number, team: Team): RUnit | null => {
    let best: RUnit | null = null, bd = Infinity;
    for (const u of m.units) {
      if (u.team !== team) continue;
      const r = STATS[u.type].r + 0.6;
      const d = dist2(wx, wy, u.x, u.y);
      if (d <= r * r && d < bd) { bd = d; best = u; }
    }
    return best;
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (e.button === 2) return; // right button → order via onContextMenu
    const c = toCanvas(e.clientX, e.clientY);
    dragRef.current = { active: true, sx: c.x, sy: c.y, cx: c.x, cy: c.y, moved: false, pointerType: e.pointerType };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    const d = dragRef.current;
    if (!d || !d.active) return;
    const c = toCanvas(e.clientX, e.clientY);
    d.cx = c.x; d.cy = c.y;
    if (Math.hypot(c.x - d.sx, c.y - d.sy) > 6) d.moved = true;
  };
  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || !d.active) return;
    const c = toCanvas(e.clientX, e.clientY);
    const w = { x: c.x / WS, y: c.y / WS };

    // Build placement intercepts the next tap/click.
    if (buildModeRef.current) { issueBuild(buildModeRef.current, w.x, w.y); setBuildMode(null); return; }

    // Box drag → select allied units inside the rectangle.
    if (d.moved) {
      const m = getModel();
      if (m) {
        const x0 = Math.min(d.sx, c.x) / WS, x1 = Math.max(d.sx, c.x) / WS;
        const y0 = Math.min(d.sy, c.y) / WS, y1 = Math.max(d.sy, c.y) / WS;
        setSelection(m.units.filter((u) => u.team === 'blue' && u.x >= x0 && u.x <= x1 && u.y >= y0 && u.y <= y1).map((u) => u.id));
      }
      return;
    }

    // Simple tap/click.
    const m = getModel();
    const friendly = m ? unitAt(m, w.x, w.y, 'blue') : null;
    if (friendly) { setSelection([friendly.id]); return; }
    // Empty tap on touch with a selection = move order (mobile "tap-move"); mouse = clear.
    if (e.pointerType === 'touch' && selRef.current.size > 0) {
      const enemy = m ? unitAt(m, w.x, w.y, 'red') : null;
      issueMove(w.x, w.y, !!enemy);
    } else {
      setSelection([]);
    }
  };
  const onContextMenu = (e: React.MouseEvent<HTMLCanvasElement>): void => {
    e.preventDefault();
    if (selRef.current.size === 0) return;
    const c = toCanvas(e.clientX, e.clientY);
    const w = { x: c.x / WS, y: c.y / WS };
    const m = getModel();
    const enemy = m ? unitAt(m, w.x, w.y, 'red') : null;
    issueMove(w.x, w.y, !!enemy); // attack-move if the click landed on an enemy
  };

  const startBattle = (): void => { sendRts('rts_start', {}); };
  const playAgain = (): void => {
    if (roleRef.current === 'host') hostReset();
    else { setBanner(null); sendRts('rts_cmd', { action: 'again' }); }
  };
  const toggleBuild = (b: BType): void => setBuildMode((cur) => (cur === b ? null : b));

  return (
    <div className="fc-root" role="group" aria-label="Flexo Commander RTS">
      <div className="fc-stage">
        <div className="fc-topbar">
          <span className="fc-title">⚔️ Flexo Commander</span>
          <span className="fc-mass">◆ {hud.mass} mass</span>
          <span className="fc-count">🔷 {hud.blue}</span>
          <span className="fc-count red">🔺 {hud.red}</span>
          {role !== 'none' && <span className={`fc-role ${role}`}>{role === 'host' ? 'HOST' : 'ALLY'}</span>}
        </div>

        <canvas
          ref={canvasRef}
          className="fc-canvas"
          width={CANVAS_W}
          height={CANVAS_H}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onContextMenu={onContextMenu}
        />

        <div className="fc-controls">
          {(['extractor', 'factory', 'turret'] as BType[]).map((b) => (
            <button
              key={b}
              type="button"
              className={`fc-btn ${buildMode === b ? 'active' : ''}`}
              disabled={!started || !selHasCmd}
              onClick={() => toggleBuild(b)}
              title={selHasCmd ? `Build ${b} (${COST[b]} mass) — then tap the map` : 'Select your commander first'}
            >
              <span className="fc-btn-glyph">{GLYPH[b]}</span>
              <span className="fc-btn-label">{b[0].toUpperCase() + b.slice(1)}</span>
              <span className="fc-btn-cost">{COST[b]}◆</span>
            </button>
          ))}
          <div className="fc-hint">
            {buildMode
              ? `Placing ${buildMode} — tap the map (extractors snap to nodes)`
              : selCount > 0
                ? `${selCount} selected · right-click / tap to move · on an enemy = attack`
                : 'Drag to select · pick your commander to build'}
          </div>
        </div>
      </div>

      {!started && (
        <div className="fc-overlay">
          <div className="fc-card">
            <span className="fc-card-icon">⚔️</span>
            <h3 className="fc-card-title">Flexo Commander</h3>
            <p className="fc-card-how">
              A co-op skirmish for two. Build extractors on the amber nodes, throw up factories and
              turrets, and mass tanks to crush the red commander — together. Whoever starts hosts the
              battle; your ally joins automatically.
            </p>
            <button type="button" className="fc-start" onClick={startBattle}>▶ Start battle together</button>
          </div>
        </div>
      )}

      {banner && (
        <div className={`fc-overlay ${banner}`}>
          <div className="fc-card">
            <span className="fc-card-icon">{banner === 'won' ? '🏆' : '💥'}</span>
            <h3 className="fc-card-title">{banner === 'won' ? 'Victory — together!' : 'Both commanders down'}</h3>
            <p className="fc-card-how">
              {banner === 'won'
                ? 'The enemy commander is destroyed. Nicely coordinated, Commanders.'
                : 'The machine overran your bases. Regroup and try a tighter defense.'}
            </p>
            <button type="button" className="fc-start" onClick={playAgain}>↻ Play again</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FlexoCommander;
